import PropTypes from "prop-types"

/**
 * PropTypes contract for single-date gatherer data with numeric summaries.
 *
 * Use this contract when the gatherer returns a simple numeric summary value
 * (e.g., a single performance metric displayed in a section header).
 *
 * For enhanced summaries with tooltips and warnings, use `singleDateDataItemContract`.
 * For TypeScript definitions, see SingleDateGathererDataItem in src/new/gatherers/types.ts.
 */
export default PropTypes.shape({
  values: PropTypes.objectOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    formatter: PropTypes.func,
  })),
  summary: PropTypes.number,
})

/**
 * PropTypes contract for individual metric value objects.
 *
 * Represents a single metric with its data, formatting, and optional enhancements.
 * Used as a building block for data item contracts.
 *
 * Matches SingleDateGathererMetricValue TypeScript interface.
 */
export const metricValueContract = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  formatter: PropTypes.func,
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  tooltipPlacement: PropTypes.oneOf(["label", "value"]),
  warning: PropTypes.string,
  isPerformanceMetric: PropTypes.bool,
})

/**
 * PropTypes contract for single-date gatherer data with enhanced metric support.
 *
 * Use this contract when the gatherer may return summaries with additional
 * metadata (tooltips, warnings) or when metric values include these enhancements.
 *
 * Summary can be either:
 * - A number (simple aggregated value)
 * - A metric object (with value, tooltip, warning, etc.)
 *
 * Matches SingleDateGathererDataItem TypeScript interface.
 */
export const singleDateDataItemContract = PropTypes.shape({
  values: PropTypes.objectOf(metricValueContract),
  summary: PropTypes.oneOfType([
    PropTypes.number,
    metricValueContract,
  ]),
})
